import React, { useEffect, useMemo, useState } from "react";
import { API, Auth, graphqlOperation } from "aws-amplify";
import { Link, useParams } from "react-router-dom";
import { listLeagues, listOrganizations, listRosterMemberships, listSeasons, listTeamPlayers, listTeams, listUserAssignments } from "../graphql/queries";
import { listPlayersWithTraits } from "../graphql/playerTraitQueries";
import { formatApiError } from "../lib/formatApiError";
import { buildAdminScope } from "../lib/adminScope";
import { encodeSharedLineupPayload } from "../lib/lineupShare";
import { buildCoachTeamIds, buildPlayerTeamIds, type RosterMembershipLike, type UserAssignmentLike } from "../lib/relationshipScope";
import PlayerTraitBadges from "../components/PlayerTraitBadges";
import "./CoachLineupPlannerPage.css";

type Team = {
  id: string;
  name: string;
  organizationId?: string | null;
  leagueId?: string | null;
  seasonId?: string | null;
  coachUsernames?: string[] | null;
};

type Organization = {
  id: string;
  name: string;
  admins?: string[] | null;
};

type League = {
  id: string;
  name: string;
  organizationId: string;
  admins?: string[] | null;
};

type Season = {
  id: string;
  name: string;
  year: number;
};

type Player = {
  id: string;
  name: string;
  teamId: string;
  pitcher?: boolean | null;
  catcher?: boolean | null;
  battingHandedness?: string | null;
  throwingHandedness?: string | null;
};

type TeamPlayerLink = {
  id: string;
  teamID: string;
  playerID: string;
};

type RosterMembership = RosterMembershipLike;

type UserAssignment = UserAssignmentLike;

type LineupPlanRecord = {
  id: string;
  teamId: string;
  seasonId?: string | null;
  title?: string | null;
  gameDate: string;
  opponentName?: string | null;
  inningsPlanned: number;
  battingOrder?: string | null;
  defensiveAssignments?: string | null;
  notes?: string | null;
  createdByUsername?: string | null;
  updatedAt?: string | null;
};

type OutfieldMode = "THREE" | "FOUR";

type PlanDraft = {
  id?: string;
  title: string;
  gameDate: string;
  opponentName: string;
  seasonId: string;
  inningsPlanned: number;
  battingOrder: string[];
  defensiveAssignments: Record<string, Record<string, string>>;
  outfieldMode: OutfieldMode;
  notes: string;
};

type LineupSummaryRow = {
  playerId: string;
  playerName: string;
  battingSlot: number | null;
  inningsPlayed: number;
  inningsSat: number;
  pitchingInnings: number;
  catchingInnings: number;
};

const INFIELD_POSITIONS = ["P", "C", "1B", "2B", "3B", "SS"] as const;
const THREE_OUTFIELD_POSITIONS = ["LF", "CF", "RF"] as const;
const FOUR_OUTFIELD_POSITIONS = ["LF", "LCF", "RCF", "RF"] as const;
const MAX_INNINGS = 9;

const FIELD_LAYOUT_THREE = [
  { key: "LF", className: "lineup-field__lf" },
  { key: "CF", className: "lineup-field__cf" },
  { key: "RF", className: "lineup-field__rf" },
  { key: "SS", className: "lineup-field__ss" },
  { key: "2B", className: "lineup-field__2b" },
  { key: "3B", className: "lineup-field__3b" },
  { key: "1B", className: "lineup-field__1b" },
  { key: "P", className: "lineup-field__p" },
  { key: "C", className: "lineup-field__c" }
] as const;

const FIELD_LAYOUT_FOUR = [
  { key: "LF", className: "lineup-field__lf" },
  { key: "LCF", className: "lineup-field__lcf" },
  { key: "RCF", className: "lineup-field__rcf" },
  { key: "RF", className: "lineup-field__rf" },
  { key: "SS", className: "lineup-field__ss" },
  { key: "2B", className: "lineup-field__2b" },
  { key: "3B", className: "lineup-field__3b" },
  { key: "1B", className: "lineup-field__1b" },
  { key: "P", className: "lineup-field__p" },
  { key: "C", className: "lineup-field__c" }
] as const;

const listLineupPlansByTeamQuery = /* GraphQL */ `
  query CoachLineupPlansByTeamId($teamId: ID!, $limit: Int, $nextToken: String) {
    coachLineupPlansByTeamId(teamId: $teamId, limit: $limit, nextToken: $nextToken) {
      items {
        id
        teamId
        seasonId
        title
        gameDate
        opponentName
        inningsPlanned
        battingOrder
        defensiveAssignments
        notes
        createdByUsername
        updatedAt
      }
      nextToken
    }
  }
`;

const createLineupPlanMutation = /* GraphQL */ `
  mutation CreateCoachLineupPlanForPage($input: CreateCoachLineupPlanInput!) {
    createCoachLineupPlan(input: $input) {
      id
    }
  }
`;

const updateLineupPlanMutation = /* GraphQL */ `
  mutation UpdateCoachLineupPlanForPage($input: UpdateCoachLineupPlanInput!) {
    updateCoachLineupPlan(input: $input) {
      id
    }
  }
`;

const deleteLineupPlanMutation = /* GraphQL */ `
  mutation DeleteCoachLineupPlanForPage($input: DeleteCoachLineupPlanInput!) {
    deleteCoachLineupPlan(input: $input) {
      id
    }
  }
`;

const parseJsonRecord = <T,>(rawValue: string | null | undefined, fallback: T): T => {
  if (!rawValue) {
    return fallback;
  }
  try {
    return JSON.parse(rawValue) as T;
  } catch {
    return fallback;
  }
};

const getOutfieldPositions = (mode: OutfieldMode) => (mode === "FOUR" ? [...FOUR_OUTFIELD_POSITIONS] : [...THREE_OUTFIELD_POSITIONS]);
const getAllFieldPositions = (mode: OutfieldMode) => [...INFIELD_POSITIONS, ...getOutfieldPositions(mode)];
const getFieldLayout = (mode: OutfieldMode) => (mode === "FOUR" ? FIELD_LAYOUT_FOUR : FIELD_LAYOUT_THREE);

const createBlankAssignments = (inningsPlanned: number, outfieldMode: OutfieldMode) => {
  const assignments: Record<string, Record<string, string>> = {};
  for (let inning = 1; inning <= inningsPlanned; inning += 1) {
    assignments[String(inning)] = getAllFieldPositions(outfieldMode).reduce<Record<string, string>>((acc, position) => {
      acc[position] = "";
      return acc;
    }, {});
  }
  return assignments;
};

const extractAssignmentsPayload = (rawValue: string | null | undefined): { outfieldMode: OutfieldMode; innings: Record<string, Record<string, string>> } => {
  const parsed = parseJsonRecord<any>(rawValue, {});
  if (parsed && typeof parsed === "object" && parsed.innings) {
    return {
      outfieldMode: parsed.meta?.outfieldMode === "FOUR" ? "FOUR" : "THREE",
      innings: parsed.innings as Record<string, Record<string, string>>
    };
  }
  return {
    outfieldMode: "THREE" as OutfieldMode,
    innings: parsed as Record<string, Record<string, string>>
  };
};

const normalizeAssignments = (rawAssignments: Record<string, Record<string, string>>, inningsPlanned: number, outfieldMode: OutfieldMode) => {
  const next = createBlankAssignments(inningsPlanned, outfieldMode);
  for (let inning = 1; inning <= inningsPlanned; inning += 1) {
    const inningKey = String(inning);
    const source = rawAssignments[inningKey] ?? {};
    for (const position of getAllFieldPositions(outfieldMode)) {
      next[inningKey][position] = source[position] ?? "";
    }
  }
  return next;
};

const createEmptyDraft = (team: Team | undefined, roster: Player[]): PlanDraft => ({
  title: "",
  gameDate: new Date().toISOString().slice(0, 10),
  opponentName: "",
  seasonId: team?.seasonId ?? "",
  inningsPlanned: 6,
  battingOrder: roster.map((player) => player.id),
  defensiveAssignments: createBlankAssignments(6, "THREE"),
  outfieldMode: "THREE",
  notes: ""
});

const planToDraft = (plan: LineupPlanRecord, roster: Player[]): PlanDraft => {
  const battingOrder = parseJsonRecord<string[]>(plan.battingOrder, []);
  const seen = new Set<string>();
  const normalizedBattingOrder = (battingOrder.length > 0 ? battingOrder : roster.map((player) => player.id)).map((playerId) => {
    if (!playerId) {
      return "";
    }
    if (seen.has(playerId)) {
      return "";
    }
    seen.add(playerId);
    return playerId;
  });
  const inningsPlanned = Math.min(MAX_INNINGS, Math.max(1, Number(plan.inningsPlanned || 6)));
  const assignmentPayload = extractAssignmentsPayload(plan.defensiveAssignments);
  const activeIds = new Set(normalizedBattingOrder.filter(Boolean));
  const cleanedAssignments = Object.fromEntries(
    Object.entries(normalizeAssignments(assignmentPayload.innings, inningsPlanned, assignmentPayload.outfieldMode)).map(([inningKey, inningAssignments]) => [
      inningKey,
      Object.fromEntries(
        Object.entries(inningAssignments).map(([position, playerId]) => [
          position,
          playerId && activeIds.has(playerId) ? playerId : ""
        ])
      )
    ])
  ) as Record<string, Record<string, string>>;
  return {
    id: plan.id,
    title: plan.title ?? "",
    gameDate: plan.gameDate,
    opponentName: plan.opponentName ?? "",
    seasonId: plan.seasonId ?? "",
    inningsPlanned,
    battingOrder: normalizedBattingOrder,
    defensiveAssignments: cleanedAssignments,
    outfieldMode: assignmentPayload.outfieldMode,
    notes: plan.notes ?? ""
  };
};

const CoachLineupPlannerPage: React.FC<{ userRoles: string[] }> = ({ userRoles }) => {
  const { teamId = "" } = useParams<{ teamId: string }>();
  const [username, setUsername] = useState("");
  const [teams, setTeams] = useState<Team[]>([]);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [leagues, setLeagues] = useState<League[]>([]);
  const [seasons, setSeasons] = useState<Season[]>([]);
  const [players, setPlayers] = useState<Player[]>([]);
  const [teamPlayers, setTeamPlayers] = useState<TeamPlayerLink[]>([]);
  const [rosterMemberships, setRosterMemberships] = useState<RosterMembership[]>([]);
  const [userAssignments, setUserAssignments] = useState<UserAssignment[]>([]);
  const [plans, setPlans] = useState<LineupPlanRecord[]>([]);
  const [selectedPlanId, setSelectedPlanId] = useState<string>("new");
  const [draft, setDraft] = useState<PlanDraft | null>(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeInning, setActiveInning] = useState(1);

  const selectedTeam = useMemo(() => teams.find((team) => team.id === teamId), [teamId, teams]);
  const adminScope = useMemo(
    () => buildAdminScope({ userRoles, username, organizations, leagues, teams, players, teamPlayers, rosterMemberships }),
    [leagues, organizations, players, rosterMemberships, teamPlayers, teams, userRoles, username]
  );
  const isCoach = userRoles.includes("Coach");
  const coachTeamIds = useMemo(
    () => buildCoachTeamIds({ username, teams, userAssignments }),
    [teams, userAssignments, username]
  );
  const isTeamCoach = Boolean(selectedTeam && coachTeamIds.has(selectedTeam.id));
  const canManageTeam = Boolean(selectedTeam && adminScope.canManageTeam(selectedTeam));
  const canUsePlanner = canManageTeam || (isCoach && isTeamCoach);

  const roster = useMemo(() => {
    const byId = new Map<string, Player>();
    const memberships = buildPlayerTeamIds({ players, teamPlayers, rosterMemberships });
    for (const player of players) {
      if ((memberships[player.id] ?? new Set<string>()).has(teamId)) {
        byId.set(player.id, player);
      }
    }
    return [...byId.values()].sort((a, b) => a.name.localeCompare(b.name));
  }, [players, rosterMemberships, teamId, teamPlayers]);

  const seasonLabelById = useMemo(
    () =>
      seasons.reduce<Record<string, string>>((acc, season) => {
        acc[season.id] = `${season.name} ${season.year}`;
        return acc;
      }, {}),
    [seasons]
  );

  const activeRoster = useMemo(() => {
    if (!draft) {
      return [];
    }
    return draft.battingOrder
      .map((playerId) => roster.find((player) => player.id === playerId) ?? null)
      .filter(Boolean) as Player[];
  }, [draft, roster]);

  const lineupSummary = useMemo<LineupSummaryRow[]>(() => {
    if (!draft) {
      return [];
    }
    const battingSlotByPlayer = new Map<string, number>();
    draft.battingOrder.forEach((playerId, index) => {
      if (playerId && !battingSlotByPlayer.has(playerId)) {
        battingSlotByPlayer.set(playerId, index + 1);
      }
    });

    return activeRoster.map((player) => {
      let inningsPlayed = 0;
      let pitchingInnings = 0;
      let catchingInnings = 0;
      for (let inning = 1; inning <= draft.inningsPlanned; inning += 1) {
        const assignments = draft.defensiveAssignments[String(inning)] ?? {};
        if (getAllFieldPositions(draft.outfieldMode).some((position) => assignments[position] === player.id)) {
          inningsPlayed += 1;
        }
        if (assignments.P === player.id) {
          pitchingInnings += 1;
        }
        if (assignments.C === player.id) {
          catchingInnings += 1;
        }
      }
      return {
        playerId: player.id,
        playerName: player.name,
        battingSlot: battingSlotByPlayer.get(player.id) ?? null,
        inningsPlayed,
        inningsSat: Math.max(0, draft.inningsPlanned - inningsPlayed),
        pitchingInnings,
        catchingInnings
      };
    });
  }, [activeRoster, draft]);

  const duplicateWarnings = useMemo(() => {
    if (!draft) {
      return [];
    }
    const warnings: string[] = [];
    for (let inning = 1; inning <= draft.inningsPlanned; inning += 1) {
      const assignments = draft.defensiveAssignments[String(inning)] ?? {};
      const counts = new Map<string, number>();
      for (const position of getAllFieldPositions(draft.outfieldMode)) {
        const playerId = assignments[position];
        if (!playerId) {
          continue;
        }
        counts.set(playerId, (counts.get(playerId) ?? 0) + 1);
      }
      for (const [playerId, count] of counts.entries()) {
        if (count > 1) {
          const playerName = activeRoster.find((player) => player.id === playerId)?.name ?? roster.find((player) => player.id === playerId)?.name ?? playerId;
          warnings.push(`Inning ${inning}: ${playerName} is assigned to multiple positions.`);
        }
      }
    }
    return warnings;
  }, [activeRoster, draft, roster]);

  const plannedBenchCount = useMemo(() => {
    if (!draft) {
      return 0;
    }
    return lineupSummary.reduce((sum, row) => sum + row.inningsSat, 0);
  }, [draft, lineupSummary]);

  const battingOrderRows = useMemo(
    () =>
      (draft?.battingOrder ?? [])
        .map((playerId, index) => ({
          slot: index + 1,
          playerName: roster.find((player) => player.id === playerId)?.name ?? ""
        }))
        .filter((row) => row.playerName),
    [draft?.battingOrder, roster]
  );

  const defenseByInningRows = useMemo(() => {
    if (!draft) {
      return [];
    }
    return Array.from({ length: draft.inningsPlanned }, (_, index) => index + 1).map((inning) => ({
      inning,
      assignments: getAllFieldPositions(draft.outfieldMode).map((position) => ({
        position,
        playerName: roster.find((player) => player.id === (draft.defensiveAssignments[String(inning)]?.[position] ?? ""))?.name ?? "-"
      }))
    }));
  }, [draft, roster]);

  const defensePrintMatrix = useMemo(() => {
    if (!draft) {
      return [];
    }
    return getAllFieldPositions(draft.outfieldMode).map((position) => ({
      position,
      byInning: Array.from({ length: draft.inningsPlanned }, (_, index) => index + 1).map((inning) =>
        roster.find((player) => player.id === (draft.defensiveAssignments[String(inning)]?.[position] ?? ""))?.name ?? "-"
      )
    }));
  }, [draft, roster]);

  const shareUrl = useMemo(() => {
    if (!draft?.id || !selectedTeam) {
      return "";
    }
    const payload = encodeSharedLineupPayload({
      version: 1,
      teamName: selectedTeam.name,
      title: draft.title.trim(),
      gameDate: draft.gameDate,
      opponentName: draft.opponentName.trim(),
      inningsPlanned: draft.inningsPlanned,
      outfieldMode: draft.outfieldMode,
      notes: draft.notes.trim(),
      battingOrder: draft.battingOrder
        .map((playerId) => roster.find((player) => player.id === playerId)?.name ?? "")
        .filter(Boolean),
      positions: getAllFieldPositions(draft.outfieldMode),
      defensiveAssignments: Array.from({ length: draft.inningsPlanned }, (_, index) => index + 1).reduce<Record<string, Record<string, string>>>((acc, inning) => {
        const inningKey = String(inning);
        acc[inningKey] = getAllFieldPositions(draft.outfieldMode).reduce<Record<string, string>>((positionAcc, position) => {
          positionAcc[position] = roster.find((player) => player.id === (draft.defensiveAssignments[inningKey]?.[position] ?? ""))?.name ?? "";
          return positionAcc;
        }, {});
        return acc;
      }, {}),
      summaryRows: lineupSummary.map((row) => ({
        playerName: row.playerName,
        battingSlot: row.battingSlot,
        inningsPlayed: row.inningsPlayed,
        inningsSat: row.inningsSat,
        pitchingInnings: row.pitchingInnings,
        catchingInnings: row.catchingInnings
      }))
    });
    return `${window.location.origin}/lineup-share/#planId=${encodeURIComponent(draft.id)}&data=${encodeURIComponent(payload)}`;
  }, [draft, lineupSummary, roster, selectedTeam]);

  const loadData = async () => {
    setLoading(true);
    setErrorMessage("");
    try {
      const authUser = await Auth.currentAuthenticatedUser();
      setUsername(authUser?.username ?? "");

      const [teamsRes, orgRes, leagueRes, seasonRes, playersRes, teamPlayersRes, rosterMembershipsRes, userAssignmentsRes] = await Promise.all([
        API.graphql(graphqlOperation(listTeams, { limit: 1000 })),
        API.graphql(graphqlOperation(listOrganizations, { limit: 1000 })),
        API.graphql(graphqlOperation(listLeagues, { limit: 1000 })),
        API.graphql(graphqlOperation(listSeasons, { limit: 1000 })),
        API.graphql(graphqlOperation(listPlayersWithTraits, { limit: 2000 })),
        API.graphql(graphqlOperation(listTeamPlayers, { limit: 5000 })),
        API.graphql(graphqlOperation(listRosterMemberships, { limit: 5000 })),
        API.graphql(graphqlOperation(listUserAssignments, { limit: 5000 }))
      ]);

      const nextTeams = ((teamsRes as any)?.data?.listTeams?.items ?? []).filter(Boolean) as Team[];
      const nextOrganizations = ((orgRes as any)?.data?.listOrganizations?.items ?? []).filter(Boolean) as Organization[];
      const nextLeagues = ((leagueRes as any)?.data?.listLeagues?.items ?? []).filter(Boolean) as League[];
      const nextSeasons = ((seasonRes as any)?.data?.listSeasons?.items ?? []).filter(Boolean) as Season[];
      const nextPlayers = ((playersRes as any)?.data?.listPlayers?.items ?? []).filter(Boolean) as Player[];
      const nextTeamPlayers = ((teamPlayersRes as any)?.data?.listTeamPlayers?.items ?? []).filter(Boolean) as TeamPlayerLink[];
      const nextRosterMemberships = ((rosterMembershipsRes as any)?.data?.listRosterMemberships?.items ?? []).filter(Boolean) as RosterMembership[];
      const nextUserAssignments = ((userAssignmentsRes as any)?.data?.listUserAssignments?.items ?? []).filter(Boolean) as UserAssignment[];

      setTeams(nextTeams);
      setOrganizations(nextOrganizations);
      setLeagues(nextLeagues);
      setSeasons(nextSeasons);
      setPlayers(nextPlayers);
      setTeamPlayers(nextTeamPlayers);
      setRosterMemberships(nextRosterMemberships);
      setUserAssignments(nextUserAssignments);

      try {
        const plansRes: any = await API.graphql(graphqlOperation(listLineupPlansByTeamQuery, { teamId, limit: 200 }));
        const nextPlans = (((plansRes as any)?.data?.coachLineupPlansByTeamId?.items ?? []).filter(Boolean) as LineupPlanRecord[]).sort((a, b) =>
          `${b.gameDate}|${b.updatedAt ?? ""}`.localeCompare(`${a.gameDate}|${a.updatedAt ?? ""}`)
        );
        setPlans(nextPlans);
      } catch (planError) {
        setPlans([]);
        setErrorMessage(`Lineup planner loaded, but saved game plans could not be loaded: ${formatApiError(planError)}`);
      }
    } catch (error) {
      setErrorMessage(`Failed to load lineup planner: ${formatApiError(error)}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [teamId]);

  useEffect(() => {
    if (!selectedTeam) {
      return;
    }
    if (selectedPlanId === "new") {
      setDraft(createEmptyDraft(selectedTeam, roster));
      setActiveInning(1);
      return;
    }
    const selectedPlan = plans.find((plan) => plan.id === selectedPlanId);
    if (selectedPlan) {
      setDraft(planToDraft(selectedPlan, roster));
      setActiveInning(1);
    } else {
      setSelectedPlanId("new");
      setDraft(createEmptyDraft(selectedTeam, roster));
      setActiveInning(1);
    }
  }, [plans, roster, selectedPlanId, selectedTeam]);

  const setInningsPlanned = (nextValue: number) => {
    setDraft((current) => {
      if (!current) {
        return current;
      }
      const inningsPlanned = Math.max(1, Math.min(MAX_INNINGS, nextValue || 1));
      return {
        ...current,
        inningsPlanned,
        defensiveAssignments: normalizeAssignments(current.defensiveAssignments, inningsPlanned, current.outfieldMode)
      };
    });
    setActiveInning((current) => Math.min(current, Math.max(1, nextValue || 1)));
  };

  const setOutfieldMode = (outfieldMode: OutfieldMode) => {
    setDraft((current) => {
      if (!current) {
        return current;
      }
      return {
        ...current,
        outfieldMode,
        defensiveAssignments: normalizeAssignments(current.defensiveAssignments, current.inningsPlanned, outfieldMode)
      };
    });
  };

  const setLineupLength = (nextValue: number) => {
    setDraft((current) => {
      if (!current) {
        return current;
      }
      const cappedLength = Math.max(1, Math.min(roster.length || 1, nextValue || 1));
      const nextOrder = [...current.battingOrder];
      if (cappedLength < nextOrder.length) {
        nextOrder.length = cappedLength;
      } else {
        while (nextOrder.length < cappedLength) {
          nextOrder.push("");
        }
      }
      const activeIds = new Set(nextOrder.filter(Boolean));
      const cleanedAssignments = Object.fromEntries(
        Object.entries(current.defensiveAssignments).map(([inningKey, inningAssignments]) => [
          inningKey,
          Object.fromEntries(
            Object.entries(inningAssignments).map(([position, playerId]) => [
              position,
              playerId && activeIds.has(playerId) ? playerId : ""
            ])
          )
        ])
      ) as Record<string, Record<string, string>>;
      return {
        ...current,
        battingOrder: nextOrder,
        defensiveAssignments: cleanedAssignments
      };
    });
  };

  const updateBattingSlot = (slotIndex: number, playerId: string) => {
    setDraft((current) => {
      if (!current) {
        return current;
      }
      const next = [...current.battingOrder];
      if (playerId) {
        for (let index = 0; index < next.length; index += 1) {
          if (index !== slotIndex && next[index] === playerId) {
            next[index] = "";
          }
        }
      }
      next[slotIndex] = playerId;
      return {
        ...current,
        battingOrder: next
      };
    });
  };

  const getAvailablePlayersForBattingSlot = (slotIndex: number) => {
    if (!draft) {
      return roster;
    }
    const unavailable = new Set<string>();
    draft.battingOrder.forEach((playerId, index) => {
      if (index !== slotIndex && playerId) {
        unavailable.add(playerId);
      }
    });
    return roster.filter((player) => !unavailable.has(player.id) || draft.battingOrder[slotIndex] === player.id);
  };

  const updatePositionAssignment = (inning: number, position: string, playerId: string) => {
    setDraft((current) => {
      if (!current) {
        return current;
      }
      const inningKey = String(inning);
      const inningAssignments = { ...(current.defensiveAssignments[inningKey] ?? {}) };
      if (playerId) {
        for (const [candidatePosition, candidatePlayerId] of Object.entries(inningAssignments)) {
          if (candidatePosition !== position && candidatePlayerId === playerId) {
            inningAssignments[candidatePosition] = "";
          }
        }
      }
      return {
        ...current,
        defensiveAssignments: {
          ...current.defensiveAssignments,
          [inningKey]: {
            ...inningAssignments,
            [position]: playerId
          }
        }
      };
    });
  };

  const getAvailablePlayersForPosition = (inning: number, position: string) => {
    if (!draft) {
      return [];
    }
    const inningAssignments = draft.defensiveAssignments[String(inning)] ?? {};
    const unavailable = new Set<string>();
    for (const [candidatePosition, playerId] of Object.entries(inningAssignments)) {
      if (candidatePosition !== position && playerId) {
        unavailable.add(playerId);
      }
    }
    return activeRoster.filter((player) => !unavailable.has(player.id) || inningAssignments[position] === player.id);
  };

  const savePlan = async () => {
    if (!draft || !selectedTeam) {
      return;
    }
    if (!draft.gameDate) {
      setErrorMessage("Game date is required.");
      return;
    }
    if (draft.battingOrder.filter(Boolean).length === 0) {
      setErrorMessage("Set at least one batting slot before saving.");
      return;
    }

    setSaving(true);
    setErrorMessage("");
    setSuccessMessage("");
    try {
      const input = {
        ...(draft.id ? { id: draft.id } : {}),
        teamId: selectedTeam.id,
        seasonId: draft.seasonId || selectedTeam.seasonId || null,
        title: draft.title.trim() || null,
        gameDate: draft.gameDate,
        opponentName: draft.opponentName.trim() || null,
        inningsPlanned: draft.inningsPlanned,
        battingOrder: JSON.stringify(draft.battingOrder),
        defensiveAssignments: JSON.stringify({
          meta: { outfieldMode: draft.outfieldMode },
          innings: draft.defensiveAssignments
        }),
        notes: draft.notes.trim() || null,
        createdByUsername: username
      };

      await API.graphql(
        graphqlOperation(draft.id ? updateLineupPlanMutation : createLineupPlanMutation, {
          input
        })
      );

      setSuccessMessage(draft.id ? "Lineup plan updated." : "Lineup plan created.");
      await loadData();
      if (!draft.id) {
        setSelectedPlanId("new");
      }
    } catch (error) {
      setErrorMessage(`Failed to save lineup plan: ${formatApiError(error)}`);
    } finally {
      setSaving(false);
    }
  };

  const deletePlan = async () => {
    if (!draft?.id || !window.confirm("Delete this lineup plan?")) {
      return;
    }
    setSaving(true);
    setErrorMessage("");
    setSuccessMessage("");
    try {
      await API.graphql(graphqlOperation(deleteLineupPlanMutation, { input: { id: draft.id } }));
      setSuccessMessage("Lineup plan deleted.");
      setSelectedPlanId("new");
      await loadData();
    } catch (error) {
      setErrorMessage(`Failed to delete lineup plan: ${formatApiError(error)}`);
    } finally {
      setSaving(false);
    }
  };

  const printReport = () => {
    window.print();
  };

  const copyShareLink = async () => {
    if (!shareUrl) {
      return;
    }
    try {
      await navigator.clipboard.writeText(shareUrl);
      setSuccessMessage("Share link copied. It opens a read-only dugout board.");
      setErrorMessage("");
    } catch {
      window.prompt("Copy this share link:", shareUrl);
    }
  };

  if (loading) {
    return <div className="max-w-6xl mx-auto"><div className="bubble-card p-4">Loading lineup planner...</div></div>;
  }

  if (!selectedTeam) {
    return (
      <div className="max-w-6xl mx-auto">
        <div className="mb-4"><Link to="/admin/teams">Back to Teams</Link></div>
        <p>Team not found.</p>
      </div>
    );
  }

  if (!canUsePlanner) {
    return (
      <div className="max-w-6xl mx-auto">
        <div className="mb-4"><Link to={`/admin/teams/${teamId}`}>Back to Team</Link></div>
        <p>You do not have access to this team's lineup planner.</p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto lineup-planner">
      <div className="mb-2"><Link to={`/admin/teams/${teamId}`}>Back to Team</Link></div>

      <div className="bubble-card p-4 lineup-planner__header">
        <div>
          <h1 className="text-2xl font-bold mb-2">Lineup Planner</h1>
          <div className="lineup-planner__meta">
            {selectedTeam.name} • Build batting order and defensive positions inning by inning.
          </div>
        </div>
        <div className="lineup-planner__actions">
          <button className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700" onClick={() => setSelectedPlanId("new")}>
            New Plan
          </button>
          {shareUrl ? (
            <>
              <a className="lineup-planner__shareButton" href={shareUrl} target="_blank" rel="noreferrer">
                Open Share Board
              </a>
              <button className="lineup-planner__shareCopyButton" onClick={copyShareLink}>
                Copy Share Link
              </button>
            </>
          ) : null}
          <button className="bg-emerald-600 text-white px-4 py-2 rounded hover:bg-emerald-700" onClick={printReport}>
            Print Report
          </button>
          <button className="bg-slate-200 text-slate-900 px-4 py-2 rounded hover:bg-slate-300" onClick={loadData}>
            Refresh
          </button>
        </div>
      </div>

      {errorMessage && <p style={{ color: "#c1121f" }}>{errorMessage}</p>}
      {successMessage && <p style={{ color: "#166534" }}>{successMessage}</p>}

      <div className="bubble-card p-4">
        <div className="user-section-header">
          <div>
            <h2>Saved Games</h2>
            <p className="text-sm text-gray-600">Use one plan per game to track who played, who sat, and where everyone lined up.</p>
          </div>
        </div>
        <div className="lineup-plan-list">
          <button
            className={`lineup-plan-card ${selectedPlanId === "new" ? "lineup-plan-card--active" : ""}`}
            onClick={() => setSelectedPlanId("new")}
          >
            <div className="lineup-plan-card__title">New Game Plan</div>
            <div className="lineup-plan-card__meta">Start a fresh lineup for this team.</div>
          </button>
          {plans.map((plan) => (
            <button
              key={plan.id}
              className={`lineup-plan-card ${selectedPlanId === plan.id ? "lineup-plan-card--active" : ""}`}
              onClick={() => setSelectedPlanId(plan.id)}
            >
              <div className="lineup-plan-card__title">{plan.title?.trim() || `Game vs ${plan.opponentName || "Opponent"}`}</div>
              <div className="lineup-plan-card__meta">{plan.gameDate} • {plan.opponentName || "Opponent TBD"}</div>
            </button>
          ))}
        </div>
      </div>

      {draft ? (
        <>
          <div className="lineup-planner__grid">
            <div className="bubble-card p-4 lineup-planner__form">
              <h2 className="text-lg font-semibold mb-3">Game Details</h2>
              <div className="lineup-planner__formGrid">
                <div className="lineup-planner__field">
                  <label>Title</label>
                  <input value={draft.title} onChange={(event) => setDraft((current) => current ? { ...current, title: event.target.value } : current)} placeholder="Opening Day, Pool Play 1..." />
                </div>
                <div className="lineup-planner__field">
                  <label>Game Date</label>
                  <input type="date" value={draft.gameDate} onChange={(event) => setDraft((current) => current ? { ...current, gameDate: event.target.value } : current)} />
                </div>
                <div className="lineup-planner__field">
                  <label>Opponent</label>
                  <input value={draft.opponentName} onChange={(event) => setDraft((current) => current ? { ...current, opponentName: event.target.value } : current)} placeholder="Riverside Eagles" />
                </div>
                <div className="lineup-planner__field">
                  <label>Season</label>
                  <select value={draft.seasonId} onChange={(event) => setDraft((current) => current ? { ...current, seasonId: event.target.value } : current)}>
                    <option value="">No season</option>
                    {seasons.map((season) => (
                      <option key={season.id} value={season.id}>{seasonLabelById[season.id]}</option>
                    ))}
                  </select>
                </div>
                <div className="lineup-planner__field">
                  <label>Planned Innings</label>
                  <select value={draft.inningsPlanned} onChange={(event) => setInningsPlanned(Number(event.target.value))}>
                    {Array.from({ length: MAX_INNINGS }, (_, index) => index + 1).map((inningCount) => (
                      <option key={inningCount} value={inningCount}>{inningCount}</option>
                    ))}
                  </select>
                </div>
                <div className="lineup-planner__field">
                  <label>Lineup Length</label>
                  <select value={draft.battingOrder.length} onChange={(event) => setLineupLength(Number(event.target.value))}>
                    {Array.from({ length: Math.max(1, roster.length) }, (_, index) => index + 1).map((slotCount) => (
                      <option key={slotCount} value={slotCount}>{slotCount}</option>
                    ))}
                  </select>
                </div>
                <div className="lineup-planner__field">
                  <label>Outfield</label>
                  <select value={draft.outfieldMode} onChange={(event) => setOutfieldMode(event.target.value as OutfieldMode)}>
                    <option value="THREE">3 Outfielders</option>
                    <option value="FOUR">4 Outfielders</option>
                  </select>
                </div>
              </div>

              <div className="lineup-planner__field" style={{ marginTop: "0.9rem" }}>
                <label>Notes</label>
                <textarea value={draft.notes} onChange={(event) => setDraft((current) => current ? { ...current, notes: event.target.value } : current)} placeholder="Bench targets, catcher limits, pitching plan..." />
              </div>

              <div className="lineup-planner__footerActions" style={{ marginTop: "1rem" }}>
                <button className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700" onClick={savePlan} disabled={saving}>
                  {saving ? "Saving..." : draft.id ? "Save Changes" : "Create Plan"}
                </button>
                {draft.id ? (
                  <button className="bg-red-600 text-white px-4 py-2 rounded hover:bg-red-700" onClick={deletePlan} disabled={saving}>
                    Delete Plan
                  </button>
                ) : null}
              </div>
            </div>

            <div className="bubble-card p-4 lineup-planner__summary">
              <h2 className="text-lg font-semibold mb-3">Game Summary</h2>
              <div className="lineup-planner__summaryGrid">
                <div className="lineup-planner__summaryCard">
                  <strong>{draft.battingOrder.length}</strong>
                  <span>Lineup players</span>
                </div>
                <div className="lineup-planner__summaryCard">
                  <strong>{draft.inningsPlanned}</strong>
                  <span>Planned innings</span>
                </div>
                <div className="lineup-planner__summaryCard">
                  <strong>{draft.outfieldMode === "FOUR" ? "4 OF" : "3 OF"}</strong>
                  <span>Defensive alignment</span>
                </div>
                <div className="lineup-planner__summaryCard">
                  <strong>{plannedBenchCount}</strong>
                  <span>Total bench innings</span>
                </div>
                <div className="lineup-planner__summaryCard">
                  <strong>{roster.length}</strong>
                  <span>Full roster</span>
                </div>
              </div>

              {duplicateWarnings.length > 0 ? (
                <div className="lineup-planner__warning">
                  {duplicateWarnings.join(" ")}
                </div>
              ) : (
                <div className="lineup-planner__rosterNote">Each player can only hold one defensive spot per inning.</div>
              )}

              <div className="lineup-planner__tableWrap" style={{ marginTop: "1rem" }}>
                <table className="lineup-planner__table">
                  <thead>
                      <tr>
                        <th>Player</th>
                        <th>Batting Slot</th>
                        <th>Innings Played</th>
                        <th>Innings Sat</th>
                        <th>P IP</th>
                        <th>C IP</th>
                      </tr>
                  </thead>
                  <tbody>
                    {lineupSummary.map((row) => (
                      <tr key={row.playerId}>
                        <td>
                          <div>{row.playerName}</div>
                          <div className="mt-1">
                            <PlayerTraitBadges player={activeRoster.find((player) => player.id === row.playerId) ?? {}} compact />
                          </div>
                        </td>
                          <td>{row.battingSlot ?? "-"}</td>
                          <td>{row.inningsPlayed}</td>
                          <td>{row.inningsSat}</td>
                          <td>{row.pitchingInnings}</td>
                          <td>{row.catchingInnings}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div className="bubble-card p-4 lineup-planner__matrix">
            <h2 className="text-lg font-semibold mb-3">Batting Order</h2>
            <div className="lineup-planner__tableWrap lineup-planner__tableWrap--batting">
              <table className="lineup-planner__table">
                <thead>
                  <tr>
                    <th>Slot</th>
                    <th>Player</th>
                  </tr>
                </thead>
                <tbody>
                  {draft.battingOrder.map((playerId, index) => (
                    <tr key={`batting-slot-${index + 1}`}>
                      <td>{index + 1}</td>
                      <td>
                        <select value={playerId} onChange={(event) => updateBattingSlot(index, event.target.value)}>
                          <option value="">Open slot</option>
                          {getAvailablePlayersForBattingSlot(index).map((player) => (
                            <option key={player.id} value={player.id}>{player.name}</option>
                          ))}
                        </select>
                        {playerId ? (
                          <div className="mt-1">
                            <PlayerTraitBadges player={roster.find((player) => player.id === playerId) ?? {}} compact />
                          </div>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                  {draft.battingOrder.length === 0 ? (
                    <tr>
                      <td colSpan={2} className="text-gray-600">No rostered players found for this team yet.</td>
                    </tr>
                  ) : null}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bubble-card p-4 lineup-planner__matrix">
            <div className="lineup-planner__defenseHeader">
              <div>
                <h2 className="text-lg font-semibold">Defense Matrix</h2>
                <div className="lineup-planner__rosterNote">Set each inning across the row. A player can only appear once per inning.</div>
              </div>
              <div className="lineup-planner__inningTabs" role="tablist" aria-label="Innings">
                {Array.from({ length: draft.inningsPlanned }, (_, index) => index + 1).map((inning) => (
                  <button
                    key={`inning-tab-${inning}`}
                    type="button"
                    role="tab"
                    aria-selected={activeInning === inning}
                    className={`lineup-planner__inningTab ${activeInning === inning ? "lineup-planner__inningTab--active" : ""}`}
                    onClick={() => setActiveInning(inning)}
                  >
                    Inning {inning}
                  </button>
                ))}
              </div>
            </div>

            <div className="lineup-planner__tableWrap">
              <table className="lineup-planner__table lineup-planner__table--matrix">
                <thead>
                  <tr>
                    <th>Position</th>
                    {Array.from({ length: draft.inningsPlanned }, (_, index) => index + 1).map((inning) => (
                      <th key={`matrix-head-${inning}`}>I{inning}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {getAllFieldPositions(draft.outfieldMode).map((position) => (
                    <tr key={`matrix-row-${position}`}>
                      <td>{position}</td>
                      {Array.from({ length: draft.inningsPlanned }, (_, index) => index + 1).map((inning) => (
                        <td key={`matrix-cell-${position}-${inning}`}>
                          <select
                            value={draft.defensiveAssignments[String(inning)]?.[position] ?? ""}
                            onChange={(event) => updatePositionAssignment(inning, position, event.target.value)}
                          >
                            <option value="">Bench / Open</option>
                            {getAvailablePlayersForPosition(inning, position).map((player) => (
                              <option key={`${position}-${inning}-${player.id}`} value={player.id}>{player.name}</option>
                            ))}
                          </select>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="lineup-planner__tableWrap" style={{ marginTop: "1rem" }}>
              <table className="lineup-planner__table">
                <thead>
                  <tr>
                    <th>Position</th>
                    <th>Active Inning</th>
                  </tr>
                </thead>
                <tbody>
                  {getAllFieldPositions(draft.outfieldMode).map((position) => (
                    <tr key={`active-inning-${activeInning}-${position}`}>
                      <td>{position}</td>
                      <td>{roster.find((player) => player.id === (draft.defensiveAssignments[String(activeInning)]?.[position] ?? ""))?.name ?? "-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <section className="lineup-print-report" aria-label="Printable game report">
            <div className="lineup-print-report__header">
              <h2>{draft.title?.trim() || `${selectedTeam.name} Game Plan`}</h2>
              <div>
                <div><strong>Team:</strong> {selectedTeam.name}</div>
                <div><strong>Date:</strong> {draft.gameDate || "-"}</div>
                <div><strong>Opponent:</strong> {draft.opponentName || "-"}</div>
                <div><strong>Innings:</strong> {draft.inningsPlanned}</div>
                <div><strong>Lineup:</strong> {draft.battingOrder.length} players</div>
                <div><strong>Outfield:</strong> {draft.outfieldMode === "FOUR" ? "4 OF" : "3 OF"}</div>
              </div>
            </div>

            <div className="lineup-print-report__grid lineup-print-report__grid--tight">
              <div>
                <h3>Batting Order</h3>
                <table className="lineup-print-report__table lineup-print-report__table--compact">
                  <thead>
                    <tr>
                      <th>Slot</th>
                      <th>Player</th>
                    </tr>
                  </thead>
                  <tbody>
                    {battingOrderRows.map((row) => (
                      <tr key={`print-bat-${row.slot}`}>
                        <td>{row.slot}</td>
                        <td>{row.playerName}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div>
                <h3>Playing Time</h3>
                <table className="lineup-print-report__table lineup-print-report__table--compact">
                  <thead>
                    <tr>
                      <th>Player</th>
                      <th>Pl</th>
                      <th>Sat</th>
                      <th>P</th>
                      <th>C</th>
                    </tr>
                  </thead>
                  <tbody>
                    {lineupSummary.map((row) => (
                      <tr key={`print-summary-${row.playerId}`}>
                        <td>{row.playerName}</td>
                        <td>{row.inningsPlayed}</td>
                        <td>{row.inningsSat}</td>
                        <td>{row.pitchingInnings}</td>
                        <td>{row.catchingInnings}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="lineup-print-report__innings">
              <h3>Defense By Inning</h3>
              <table className="lineup-print-report__table lineup-print-report__table--compact lineup-print-report__table--defense">
                <thead>
                  <tr>
                    <th>Pos</th>
                    {defenseByInningRows.map((inningRow) => (
                      <th key={`defense-head-${inningRow.inning}`}>I{inningRow.inning}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {defensePrintMatrix.map((row) => (
                    <tr key={`defense-row-${row.position}`}>
                      <td>{row.position}</td>
                      {row.byInning.map((playerName, index) => (
                        <td key={`defense-cell-${row.position}-${index + 1}`}>{playerName}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {draft.notes.trim() ? (
              <div className="lineup-print-report__notes">
                <h3>Notes</h3>
                <p>{draft.notes}</p>
              </div>
            ) : null}
          </section>
        </>
      ) : null}
    </div>
  );
};

export default CoachLineupPlannerPage;
